import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ProductCard } from "../components/product/ProductCard";
import { MagnifyingGlass, FunnelSimple, X } from "@phosphor-icons/react";
import { usePageMeta } from "../lib/usePageMeta";
import NotFound from "./NotFound";
import { ProductFamilyCard, groupIntoFamilies } from "../components/product/ProductFamilyCard";
import { CATEGORY_META, KNOWN_CATEGORY_SLUGS, SHOP_META } from "../lib/categoryMeta";
import { RESEARCH_AREAS, productsInArea } from "../lib/research-areas";
import { trackSearch } from "../lib/analytics";
import { readPreload } from "../lib/preload";

// Values must match products.format exactly (backend filters by equality).
const FORMAT_OPTIONS = [
  { id: "all", value: "", label: "All formats" },
  { id: "lyophilized-vial", value: "Lyophilized reference standard", label: "Lyophilized vial" },
  { id: "tablets", value: "Tablet", label: "Tablets" },
  { id: "aqueous-solution", value: "Aqueous Reference Solution", label: "Aqueous solution" },
];

export default function ShopAll() {
  const [params, setParams] = useSearchParams();
  const { categorySlug: pathCategorySlug } = useParams();
  const navigate = useNavigate();

  // Path-segment routing takes precedence over legacy ?category= query string
  const category = pathCategorySlug || params.get("category") || "";
  const search = params.get("search") || "";
  const format = params.get("format") || "";
  // Prerendered snapshots embed the unfiltered list (see scripts/prerender.mjs).
  const [preloaded] = useState(() => (search || format ? undefined
    : readPreload(`/products?${new URLSearchParams({ ...(category && { category }), limit: 200 })}`)));
  const [items, setItems] = useState(() => preloaded?.items || []);
  const [total, setTotal] = useState(() => preloaded?.total || 0);
  const [categories, setCategories] = useState(() => readPreload("/categories") || []);
  const [loading, setLoading] = useState(!preloaded);
  const skipLoadingFlash = useRef(!!preloaded);
  // Plain-English "research area" filter — presentation-only, layered
  // on top of the fetched product list without touching the backend.
  const areaId = params.get("area") || "";
  const activeArea = areaId ? RESEARCH_AREAS.find((a) => a.id === areaId) : null;
  const [searchInput, setSearchInput] = useState(search);

  // Category meta — DETERMINISTIC. Sourced from a hand-authored static map so
  // prerender snapshots always contain the correct title/description/JSON-LD
  // even if `/api/categories` has not returned yet. `catObj` is only used for
  // DB-driven display metadata (e.g. count in the sidebar).
  const staticCat = category ? CATEGORY_META[category] : null;
  const catObj = categories.find((c) => c.slug === category);
  // Unknown-category detection: category param present, is NOT in the
  // hand-authored map, AND categories fetched (or fetch idle) → treat as 404.
  const categoryUnknown = !!category && !staticCat;

  const categoryName = staticCat?.name || catObj?.name || (category
    ? category.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    : "");
  const titleBase = category
    ? `${categoryName} · Research Compounds`
    : (search ? `Search "${search}"` : "Shop · All Research Compounds");
  const descBase = category
    ? (staticCat?.description || catObj?.description || catObj?.seo_description ||
        `${categoryName} peptide reference standards from Medical Peptide Store. In vitro research use only. Signature-required shipping, lot documentation available.`)
    : SHOP_META.description;

  const canonicalHost = "https://medicalpeptidestore.com";
  const canonicalPath = category ? `/shop/category/${category}` : "/shop";
  const canonicalUrl = `${canonicalHost}${canonicalPath}`;

  // Structured data — CollectionPage + BreadcrumbList emit DETERMINISTICALLY
  // for any known category (they do not depend on the async items fetch).
  // ItemList is only emitted once items have arrived (dynamic content).
  const categoryJsonLd = useMemo(() => {
    if (!category || !staticCat) return [];
    const blocks = [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": titleBase,
        "description": descBase,
        "url": canonicalUrl,
        "inLanguage": "en-US",
        "isPartOf": { "@id": `${canonicalHost}/#website` },
        "about": { "@id": `${canonicalHost}/#organization` },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${canonicalHost}/` },
          { "@type": "ListItem", "position": 2, "name": "Catalog", "item": `${canonicalHost}/shop` },
          { "@type": "ListItem", "position": 3, "name": categoryName, "item": canonicalUrl },
        ],
      },
    ];
    if (items.length > 0) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": `${categoryName} — reference standards`,
        "numberOfItems": items.length,
        "itemListElement": items.map((p, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "url": `${canonicalHost}/product/${p.slug}`,
          "name": p.public_display_name || p.product_name,
        })),
      });
    }
    return blocks;
  }, [category, staticCat, titleBase, descBase, canonicalUrl, categoryName, items]);

  usePageMeta({
    title: titleBase,
    description: descBase,
    canonicalPath,
    robots: search || categoryUnknown ? "noindex, follow" : undefined,
    jsonLd: categoryUnknown ? [] : categoryJsonLd,
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    if (skipLoadingFlash.current) skipLoadingFlash.current = false;
    else setLoading(true);
    const p = { category: category || undefined, search: search || undefined, format: format || undefined, limit: 200 };
    api.get("/products", { params: p })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [category, search, format]);

  // Debounced GA4 `search` event — fires 400 ms after the URL-committed
  // ?search= value settles, only when the query is ≥3 chars, and only
  // when it differs from the last emitted value (dedupe handled inside
  // `trackSearch`). NEVER mirrored to Clarity — the whitelist in
  // `lib/clarity.js` drops the query string.
  useEffect(() => {
    if (!search || search.length < 3) return;
    const t = setTimeout(() => {
      try { trackSearch(search); } catch (_) {}
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Category links are real hrefs (crawlable); other filters keep their query.
  const categoryHref = (slug) => {
    const qs = new URLSearchParams(params);
    qs.delete("category");
    const s = qs.toString();
    return `${slug ? `/shop/category/${slug}` : "/shop"}${s ? `?${s}` : ""}`;
  };

  const updateParam = (key, value) => {
    // Category selection navigates to path-segment route for SEO uniqueness.
    if (key === "category") {
      navigate(categoryHref(value));
      return;
    }
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const activeFilters = useMemo(() => {
    const f = [];
    if (activeArea) f.push({ key: "area", label: activeArea.label });
    if (category) {
      const c = categories.find((x) => x.slug === category);
      if (c) f.push({ key: "category", label: c.name });
    }
    if (search) f.push({ key: "search", label: `“${search}”` });
    if (format) f.push({ key: "format", label: FORMAT_OPTIONS.find((o) => o.value === format)?.label || format });
    return f;
  }, [activeArea, category, search, format, categories]);

  const families = useMemo(() => groupIntoFamilies(items), [items]);

  // Layer the plain-English "research area" filter on top of the fetched
  // list.  This is a pure presentation filter — the backend still returns
  // the same items; we just hide the ones that don't fall into the picked
  // area.  When no area is picked, `visibleItems` = the full `items` list.
  const visibleItems = useMemo(() => {
    if (!activeArea) return items;
    return productsInArea(items, activeArea.id);
  }, [items, activeArea]);
  const visibleFamilies = useMemo(
    () => (activeArea ? groupIntoFamilies(visibleItems) : families),
    [visibleItems, families, activeArea],
  );

  // Unknown category slug — render the same NotFound UX as an unknown route.
  // (Placed after all hook calls to satisfy react-hooks/rules-of-hooks.)
  if (categoryUnknown) return <NotFound />;

  return (
    <div className="bg-white" data-testid="shop-all-page">
      {/* Page header — light hero */}
      <section className="border-b border-brand-border bg-brand-surface">
        <div className="mps-container py-14 md:py-20">
          <div className="max-w-3xl">
            <div className="eyebrow mb-4">Catalog</div>
            <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight text-brand-navy leading-tight" data-testid="shop-h1">
              {activeArea ? activeArea.label : (category ? categoryName : "All research compounds")}
            </h1>
            <p className="mt-5 text-lg text-brand-muted leading-relaxed max-w-2xl">
              {activeArea
                ? activeArea.description
                : (category
                    ? descBase
                    : "Browse the complete catalog. Availability, eligibility, and purchasing requirements depend on the product, customer type, intended use, and destination.")}
            </p>
          </div>
        </div>
      </section>

      <div className="mps-container py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Filters rail */}
        <aside className="lg:col-span-3">
          <div className="mps-card p-6 lg:p-7 sticky top-32 lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto" data-testid="shop-filters-rail">
            <div className="flex items-center gap-2 mb-6 text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-navy">
              <FunnelSimple size={14} weight="regular" /> Filters
            </div>

            <div className="mb-8">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-muted mb-3">Search</div>
              <form onSubmit={(e) => { e.preventDefault(); updateParam("search", searchInput.trim()); }} className="relative">
                <MagnifyingGlass size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-muted" weight="regular" aria-hidden />
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  data-testid="shop-search-input"
                  placeholder="e.g. BPC-157"
                  className="w-full rounded-[12px] bg-white border border-brand-border pl-10 pr-3 py-2.5 text-sm text-brand-text placeholder:text-brand-muted focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 focus:outline-none transition-colors"
                />
              </form>
            </div>

            <div className="mb-8">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-muted mb-3">Category</div>
              <ul className="space-y-0.5">
                <li>
                  <Link
                    to={categoryHref("")}
                    data-testid="filter-category-all"
                    className={`block w-full text-left text-[14px] py-2 px-3 rounded-lg transition-colors ${category === "" ? "bg-brand-navy text-white font-semibold" : "text-brand-text hover:bg-brand-surface"}`}
                  >
                    All categories
                  </Link>
                </li>
                {categories.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={categoryHref(c.slug)}
                      data-testid={`filter-category-${c.slug}`}
                      className={`block w-full text-left text-[14px] py-2 px-3 rounded-lg transition-colors ${category === c.slug ? "bg-brand-navy text-white font-semibold" : "text-brand-text hover:bg-brand-surface"}`}
                    >
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-muted mb-3">Format</div>
              <div className="flex flex-col gap-0.5">
                {FORMAT_OPTIONS.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => updateParam("format", o.value)}
                    data-testid={`filter-format-${o.id}`}
                    className={`text-left text-[14px] py-2 px-3 rounded-lg transition-colors ${format === o.value ? "bg-brand-navy text-white font-semibold" : "text-brand-text hover:bg-brand-surface"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-9">
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <span className="text-sm font-semibold text-brand-navy" data-testid="shop-result-count">
              {loading ? "…" : (params.get("view") === "list" ? visibleItems.length : visibleFamilies.length) + " " + (params.get("view") === "list" ? "SKU" + (visibleItems.length === 1 ? "" : "s") : "compound famil" + (visibleFamilies.length === 1 ? "y" : "ies"))}
              {params.get("view") !== "list" && visibleItems.length !== visibleFamilies.length && (
                <span className="ml-1 text-brand-muted font-normal">· {visibleItems.length} SKUs</span>
              )}
            </span>
            <div className="inline-flex rounded-full border border-brand-border bg-white overflow-hidden" data-testid="shop-view-toggle">
              <button
                type="button"
                onClick={() => updateParam("view", "")}
                data-testid="view-toggle-family"
                className={`text-xs font-semibold px-3 py-1.5 transition-colors ${(params.get("view") || "family") === "family" ? "bg-brand-navy text-white" : "text-brand-navy hover:bg-brand-surface"}`}
              >
                By compound
              </button>
              <button
                type="button"
                onClick={() => updateParam("view", "list")}
                data-testid="view-toggle-list"
                className={`text-xs font-semibold px-3 py-1.5 transition-colors ${params.get("view") === "list" ? "bg-brand-navy text-white" : "text-brand-navy hover:bg-brand-surface"}`}
              >
                All SKUs
              </button>
            </div>
            {activeFilters.map((f) => (
              <button
                key={f.key}
                onClick={() => updateParam(f.key, "")}
                data-testid={`active-filter-${f.key}`}
                className="inline-flex items-center gap-1.5 bg-brand-surface border border-brand-border text-brand-navy text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-white hover:border-brand-navy transition-colors"
              >
                {f.label} <X size={10} weight="bold" />
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`shimmer-${i}`} className="h-[420px] mps-card mps-shimmer" />
              ))}
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="mps-card p-16 text-center" data-testid="shop-empty-state">
              <div className="font-display text-xl font-semibold text-brand-navy">No products match those filters.</div>
              <p className="mt-3 text-brand-muted">Try adjusting your search terms or clearing filters.</p>
            </div>
          ) : (params.get("view") || "family") === "family" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8" data-testid="shop-results-family">
              {visibleFamilies.map((f) => <ProductFamilyCard key={f.key} family={f} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8" data-testid="shop-results">
              {visibleItems.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
